import {
    AccountingReportCycle,
    AccountingReportCycleTotal,
} from "@common/constants";
import {
    AccountingReportComparisonOptions,
    AccountingReportType,
    ACCOUNTING_REPORT_COMPARISON_OPTIONS,
    NO_ACCOUNTING_REPORT_COMPARISON_OPTIONS,
} from "@common/types/reports/report-type";
import { ViewType } from "@common/types/views/views";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import { AccountingReportFilters } from "../types";

export type AccountingReportViewId =
    | "profitLoss"
    | "balanceSheet"
    | "generalLedger"
    | "trialBalance"
    | "cashFlowStatement"
    | "expenseByVendor";

export interface AccountingReportViewDefinition extends AccountingReportComparisonOptions {
    type: ViewType;
    reportType: AccountingReportType;
    cacheKey: WORKSPACE_USER_CACHE_KEYS;
    defaultCycle?: AccountingReportCycle | AccountingReportCycleTotal;
    hasCounterparties: boolean;
    hasFinancialAccounts: boolean;
    hasGeneralLedgerFilters: boolean;
    hasTaxView: boolean;
    hasLedger: boolean;
    canConsolidate: boolean;
    hasConsolidation: boolean;
}

const PAGE_ROWS: Record<
    AccountingReportViewId,
    Omit<
        AccountingReportViewDefinition,
        | "hasLedger"
        | "canConsolidate"
        | "hasConsolidation"
        | "comparisons"
        | "comparisonDiffs"
        | "ratioTypes"
    >
> = {
    profitLoss: {
        type: ViewType.PROFIT_LOSS,
        cacheKey:
            WORKSPACE_USER_CACHE_KEYS.PROFIT_LOSS_PAGE_BASE_VIEW_ID_CACHE_KEY,
        defaultCycle: AccountingReportCycle.MONTH,
        reportType: AccountingReportType.PROFIT_LOSS,
        hasCounterparties: true,
        hasFinancialAccounts: true,
        hasGeneralLedgerFilters: false,
        hasTaxView: true,
    },
    balanceSheet: {
        type: ViewType.BALANCE_SHEET,
        cacheKey:
            WORKSPACE_USER_CACHE_KEYS.BALANCE_SHEET_PAGE_BASE_VIEW_ID_CACHE_KEY,
        defaultCycle: AccountingReportCycleTotal.TOTAL,
        reportType: AccountingReportType.BALANCE_SHEET,
        hasCounterparties: true,
        hasFinancialAccounts: true,
        hasGeneralLedgerFilters: false,
        hasTaxView: true,
    },
    generalLedger: {
        type: ViewType.GENERAL_LEDGER,
        cacheKey:
            WORKSPACE_USER_CACHE_KEYS.GENERAL_LEDGER_PAGE_BASE_VIEW_ID_CACHE_KEY,
        reportType: AccountingReportType.GENERAL_LEDGER,
        hasCounterparties: true,
        hasFinancialAccounts: true,
        hasGeneralLedgerFilters: true,
        hasTaxView: false,
    },
    trialBalance: {
        type: ViewType.TRIAL_BALANCE,
        cacheKey:
            WORKSPACE_USER_CACHE_KEYS.TRIAL_BALANCE_PAGE_BASE_VIEW_ID_CACHE_KEY,
        defaultCycle: AccountingReportCycle.MONTH,
        reportType: AccountingReportType.TRIAL_BALANCE,
        hasCounterparties: true,
        hasFinancialAccounts: true,
        hasGeneralLedgerFilters: false,
        hasTaxView: false,
    },
    cashFlowStatement: {
        type: ViewType.CASH_FLOW_STATEMENT,
        cacheKey:
            WORKSPACE_USER_CACHE_KEYS.CASH_FLOW_STATEMENT_PAGE_BASE_VIEW_ID_CACHE_KEY,
        defaultCycle: AccountingReportCycle.MONTH,
        reportType: AccountingReportType.CASH_FLOW_STATEMENT,
        hasCounterparties: true,
        hasFinancialAccounts: true,
        hasGeneralLedgerFilters: false,
        hasTaxView: false,
    },
    expenseByVendor: {
        type: ViewType.EXPENSE_BY_VENDOR,
        cacheKey:
            WORKSPACE_USER_CACHE_KEYS.EXPENSE_BY_VENDOR_PAGE_BASE_VIEW_ID_CACHE_KEY,
        defaultCycle: AccountingReportCycle.MONTH,
        reportType: AccountingReportType.EXPENSE_BY_VENDOR,
        hasCounterparties: false,
        hasFinancialAccounts: true,
        hasGeneralLedgerFilters: false,
        hasTaxView: false,
    },
};

export function getAccountingReportViewDefinition(
    reportId: AccountingReportViewId,
    filters: Pick<AccountingReportFilters, "consolidationGroupId">,
): AccountingReportViewDefinition {
    const row = PAGE_ROWS[reportId];
    const isConsolidated = Boolean(filters.consolidationGroupId);
    const isStatementWithConsolidation =
        reportId === "profitLoss" || reportId === "balanceSheet";
    const hasConsolidation = isStatementWithConsolidation && isConsolidated;

    return {
        ...row,
        hasLedger: reportId === "generalLedger" || !isConsolidated,
        canConsolidate: isStatementWithConsolidation,
        hasConsolidation,
        ...(hasConsolidation
            ? NO_ACCOUNTING_REPORT_COMPARISON_OPTIONS
            : ACCOUNTING_REPORT_COMPARISON_OPTIONS[row.reportType]),
        defaultCycle: hasConsolidation ? undefined : row.defaultCycle,
    };
}
